// Single source of truth for the app version + changelog.
//
// CONVENTION: add a NEW entry at the TOP for every change and bump the version
// (SemVer: patch = fix, minor = feature, major = breaking), then set the same
// version in package.json. APP_VERSION is derived from the newest entry, and the
// "What's new" dialog auto-shows entries the user has not seen yet.
//
// Entries carry both languages inline rather than going through t(): they are
// write-once prose that would otherwise dump dozens of one-off keys into
// src/i18n/translations/ — where check-i18n.mjs would then flag each of them the
// moment an entry scrolls out of relevance. The dialog's own chrome IS translated.

export interface ChangelogText {
  en: string
  de: string
}

export interface ChangelogEntry {
  version: string // e.g. "1.10.0"
  date: string // ISO date, e.g. "2026-07-29"
  title: ChangelogText // short headline for this release
  changes: ChangelogText[] // user-facing bullet points
}

// Newest first. Entries before 1.10.0 were reconstructed from the git history on
// 2026-07-29, when the changelog was introduced — dates are the commit dates.
export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '1.10.0',
    date: '2026-07-29',
    title: {
      en: 'Version badge now shows what changed',
      de: 'Versionsschild zeigt, was sich geändert hat',
    },
    changes: [
      {
        en: 'Click the version badge next to FLIXALIO in the header to open a "What\'s new" list. After an update it opens once by itself and shows only what you have not seen yet.',
        de: 'Ein Klick auf das Versionsschild neben FLIXALIO in der Kopfzeile öffnet eine „Was ist neu"-Liste. Nach einem Update öffnet sie sich einmal von selbst und zeigt nur, was du noch nicht gesehen hast.',
      },
      {
        en: 'The releases below were reconstructed from the development history, so the list starts with the first version rather than with today.',
        de: 'Die Einträge darunter wurden aus der Entwicklungshistorie nachgetragen, damit die Liste bei der ersten Version anfängt und nicht erst heute.',
      },
    ],
  },
  {
    version: '1.9.1',
    date: '2026-07-29',
    title: {
      en: 'Explanatory lines under the page titles removed',
      de: 'Erklärsätze unter den Seitentiteln entfernt',
    },
    changes: [
      {
        en: 'Dashboard, Import data, Invoice Analyser, Inbox and PO Tracking no longer carry a sentence under their title — the title alone says where you are.',
        de: 'Dashboard, Daten importieren, Invoice Analyser, Inbox und PO Tracking haben keinen Satz mehr unter der Überschrift — die Überschrift allein sagt, wo du bist.',
      },
      {
        en: 'Database keeps its line, because that one carries live counts (invoices, suppliers, custom columns) rather than an explanation.',
        de: 'Database behält seine Zeile, weil dort Live-Zahlen stehen (Rechnungen, Lieferanten, eigene Spalten) und keine Erklärung.',
      },
    ],
  },
  {
    version: '1.9.0',
    date: '2026-07-29',
    title: { en: 'German and English, switchable', de: 'Deutsch und Englisch, umschaltbar' },
    changes: [
      {
        en: 'The whole interface is available in German and English. Switch with the DE/EN button in the header; the choice is remembered.',
        de: 'Die ganze Oberfläche gibt es auf Deutsch und Englisch. Umschalten über die DE/EN-Schaltfläche in der Kopfzeile; die Wahl bleibt gespeichert.',
      },
    ],
  },
  {
    version: '1.8.1',
    date: '2026-07-28',
    title: { en: 'All POs selectable in the Analyser', de: 'Alle POs im Analyser wählbar' },
    changes: [
      {
        en: 'The PO selector in the Invoice Analyser offers every purchase order again, not just a subset.',
        de: 'Die PO-Auswahl im Invoice Analyser bietet wieder alle Purchase Orders an, nicht nur einen Teil.',
      },
      {
        en: 'The hint lines under those selectors are gone — they repeated what the selector already showed.',
        de: 'Die Hinweiszeilen unter diesen Auswahlfeldern sind weg — sie wiederholten, was das Feld schon zeigte.',
      },
    ],
  },
  {
    version: '1.8.0',
    date: '2026-07-28',
    title: {
      en: 'Hand invoices to Flixpector from the Analyser',
      de: 'Übergabe an Flixpector aus dem Analyser',
    },
    changes: [
      {
        en: 'An invoice reviewed in the Invoice Analyser can be pushed straight to Flixpector for the law-firm review.',
        de: 'Eine im Invoice Analyser geprüfte Rechnung lässt sich direkt an Flixpector zur Kanzleiprüfung übergeben.',
      },
      {
        en: 'A handler has to be picked before the hand-over — the initials travel along and show up on every line in Flixpector.',
        de: 'Vor der Übergabe muss ein Bearbeiter gewählt werden — das Kürzel wird mitgegeben und erscheint in Flixpector auf jeder Zeile.',
      },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-07-28',
    title: { en: 'Email intake and the Inbox', de: 'E-Mail-Eingang und die Inbox' },
    changes: [
      {
        en: 'Invoices and service records that arrive by email land in a new Inbox instead of a mailbox nobody watches.',
        de: 'Per E-Mail eingehende Rechnungen und Leistungsnachweise landen in einer neuen Inbox statt in einem Postfach, das niemand im Blick hat.',
      },
      {
        en: 'Every entry has a state — open, with Flixpector, back from Flixpector, in the database, filed without a document — so nothing is booked before you decide.',
        de: 'Jeder Eintrag hat einen Zustand — offen, bei Flixpector, zurück von Flixpector, in der Datenbank, ohne Beleg abgelegt — damit nichts gebucht wird, bevor du entschieden hast.',
      },
      {
        en: 'The detail view shows the original document, the extracted data and the Flixpector result including its risk score.',
        de: 'Die Detailansicht zeigt den Originalbeleg, die ausgelesenen Daten und das Flixpector-Ergebnis samt Risiko-Score.',
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-06-23',
    title: { en: 'Redesigned interface', de: 'Überarbeitete Oberfläche' },
    changes: [
      {
        en: 'Calmer dashboard: the key figures collapse into a summary bar, so the charts start above the fold.',
        de: 'Ruhigeres Dashboard: die Kennzahlen klappen in eine Übersichtszeile zusammen, damit die Charts oben anfangen.',
      },
      {
        en: 'Database got filters and column management; PO Tracking got a set of fixes.',
        de: 'Database hat Filter und Spaltenverwaltung bekommen, PO Tracking eine Reihe von Korrekturen.',
      },
      {
        en: 'New look throughout, with the FLIXALIO brand in the header.',
        de: 'Durchgehend neues Erscheinungsbild, mit der Marke FLIXALIO in der Kopfzeile.',
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-06-23',
    title: { en: 'Bulk approval and due-date reminders', de: 'Sammelfreigabe und Fälligkeits-Erinnerungen' },
    changes: [
      {
        en: 'Approve several invoices in one go instead of one after another.',
        de: 'Mehrere Rechnungen in einem Zug freigeben statt eine nach der anderen.',
      },
      {
        en: 'Invoices coming due and overdue ones are flagged, and the dashboard lists what needs attention first.',
        de: 'Fällige und überfällige Rechnungen werden markiert, und das Dashboard listet zuerst auf, was Aufmerksamkeit braucht.',
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-06-23',
    title: { en: 'Status and approval workflow', de: 'Status- und Freigabe-Workflow' },
    changes: [
      {
        en: 'Every invoice carries a status through the review, from open to approved for payment.',
        de: 'Jede Rechnung trägt einen Status durch die Prüfung, von offen bis zahlungsfrei.',
      },
      {
        en: 'Table and dashboard were quietened down so the status is what stands out.',
        de: 'Tabelle und Dashboard wurden zurückgenommen, damit der Status heraussticht.',
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-01-18',
    title: { en: 'PO Tracking', de: 'PO-Tracking' },
    changes: [
      {
        en: 'Budget consumption per purchase order, with a forecast to year end.',
        de: 'Budgetverbrauch pro Purchase Order, mit Prognose bis Jahresende.',
      },
      {
        en: 'The chart marks the individual invoices on the cumulative line, and the line type can be switched.',
        de: 'Der Chart markiert die einzelnen Rechnungen auf der Verlaufslinie, und die Linienart lässt sich umschalten.',
      },
      {
        en: 'Years can be added by hand, so a new budget year does not have to wait for the first invoice.',
        de: 'Jahre lassen sich selbst anlegen, ein neues Budgetjahr muss also nicht auf die erste Rechnung warten.',
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-01-16',
    title: { en: 'Duplicate invoices are caught', de: 'Doppelte Rechnungen werden erkannt' },
    changes: [
      {
        en: 'Before an extracted invoice goes into the database, it is checked against what is already there — same supplier and invoice number raises a warning instead of a second record.',
        de: 'Bevor eine ausgelesene Rechnung in die Datenbank geht, wird gegen den Bestand geprüft — gleicher Lieferant und gleiche Rechnungsnummer führen zu einer Warnung statt zu einem zweiten Datensatz.',
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-01-16',
    title: {
      en: 'VAT breakdown, and documents are kept',
      de: 'USt-Aufschlüsselung, und Belege bleiben erhalten',
    },
    changes: [
      {
        en: 'The Analyser reads VAT, tax-free and taxable net amounts out of the invoice, not just the total.',
        de: 'Der Analyser liest USt-, steuerfreie und steuerpflichtige Nettobeträge aus der Rechnung, nicht nur die Summe.',
      },
      {
        en: 'Uploaded documents and their extraction history are stored permanently and survive a server restart.',
        de: 'Hochgeladene Belege und ihr Auswerte-Verlauf werden dauerhaft gespeichert und überstehen einen Server-Neustart.',
      },
    ],
  },
  {
    version: '1.0.0',
    date: '2026-01-15',
    title: { en: 'First version', de: 'Erste Version' },
    changes: [
      {
        en: 'Sign-in with username and password, plus a user management screen for admins.',
        de: 'Anmeldung mit Benutzername und Passwort, dazu eine Benutzerverwaltung für Admins.',
      },
      {
        en: 'Dashboard with key figures, charts and filters over the invoice data.',
        de: 'Dashboard mit Kennzahlen, Charts und Filtern über die Rechnungsdaten.',
      },
      {
        en: 'Invoice database with custom columns, Excel import and export.',
        de: 'Rechnungsdatenbank mit eigenen Spalten, Excel-Import und Export.',
      },
      {
        en: 'Invoice Analyser: upload a document, read the data out of it, check the evidence.',
        de: 'Invoice Analyser: Beleg hochladen, Daten daraus auslesen, Belegstellen prüfen.',
      },
    ],
  },
]

export const APP_VERSION: string = CHANGELOG[0]?.version ?? '1.0.0'

// The changelog is what the UI shows, but package.json carries the version too
// (vite.config.ts injects it as __APP_VERSION__). Bumping one and forgetting the
// other is the obvious way to get this wrong, so say so in dev instead of letting
// the two drift apart unnoticed.
if (import.meta.env.DEV && __APP_VERSION__ !== APP_VERSION) {
  console.warn(
    `[changelog] package.json is at ${__APP_VERSION__} but the newest changelog entry is ${APP_VERSION} — bump both.`,
  )
}

/** localStorage key for the last changelog version the user has acknowledged. */
export const LAST_SEEN_VERSION_KEY = 'flixalio:lastSeenVersion'

/** Custom DOM event any component can dispatch to (re-)open the changelog dialog. */
export const OPEN_CHANGELOG_EVENT = 'flixalio:open-changelog'

/** Compares two SemVer-ish strings ("1.2.0"): >0 if a>b, <0 if a<b, 0 if equal. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0)
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (diff !== 0) return diff
  }
  return 0
}
